import { Icon } from '@mui/material';
import { FormShell, MessageAlert, RBButton, RbTable, Timeline, VirtualTree } from '@scmlatam/web-ui';
import type { IAssignHiringDialogProps, IHiringRow } from '../types/hooks';

type FormProps = IAssignHiringDialogProps['formProps'];

const HeaderAlert = ({ formProps }: { formProps: FormProps }) => {
    if (!formProps.showMessage) return null;
    return (
        <MessageAlert
            type="warning"
            title={formProps.t('assign_hiring.alerts.unsaved_changes.title')}
            message={formProps.t('assign_hiring.alerts.unsaved_changes.message')}
            className="rb-message-alert--form"
            actions={[
                {
                    label: formProps.t('assign_hiring.alerts.unsaved_changes.action_button'),
                    onClick: () => formProps.setShowMessage(false),
                },
                {
                    label: formProps.t('assign_hiring.alerts.unsaved_changes.cancel_button'),
                    onClick: () => {
                        formProps.setShowMessage(false);
                        formProps.closeForm(true);
                    },
                },
            ]}
        />
    );
};

const Footer = ({ formProps }: { formProps: FormProps }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3">
        <RBButton variant="secondary" size="s" disabled={formProps.loading || formProps.saving} onClick={formProps.addRow} rightIcon={<Icon baseClassName="material-symbols-rounded">add</Icon>}>
            {formProps.t('assign_hiring.buttons.new_row')}
        </RBButton>

        <div className="flex items-center gap-3">
            <RBButton variant="secondary" size="m" disabled={formProps.saving} onClick={() => formProps.closeForm(false)}>
                {formProps.t('assign_hiring.buttons.cancel')}
            </RBButton>
            <RBButton size="m" loading={formProps.saving} disabled={!formProps.canSubmit} onClick={formProps.persistRows}>
                {formProps.t('assign_hiring.buttons.save')}
            </RBButton>
        </div>
    </div>
);

const PositionTreeFooter = ({ formProps }: { formProps: FormProps }) => (
    <div className="flex items-center justify-end gap-3 p-3">
        <RBButton variant="secondary" size="s" onClick={formProps.closePositionTree}>
            {formProps.t('assign_hiring.buttons.cancel')}
        </RBButton>
        <RBButton size="s" disabled={!formProps.selectedPosition} onClick={formProps.confirmPositionSelection}>
            {formProps.t('assign_hiring.buttons.assign')}
        </RBButton>
    </div>
);

const PositionTreeDialog = ({ formProps }: { formProps: FormProps }) => (
    <FormShell open={formProps.openTree} title={formProps.t('assign_hiring.placeholder.position')} size="m" onClose={formProps.closePositionTree} footer={<PositionTreeFooter formProps={formProps} />}>
        <VirtualTree
            items={formProps.positionTreeItems}
            selectionMode="single"
            selectedIds={formProps.selectedPosition ? [formProps.selectedPosition.nodeId] : []}
            onSelectionChange={formProps.handlePositionSelectionChange}
            defaultExpandAll
            loading={formProps.loading}
            emptyText={formProps.t('assign_hiring.table.empty')}
        />
    </FormShell>
);

const AssignHiringDialog = ({ formProps }: IAssignHiringDialogProps) => {
    return (
        <>
            <FormShell
                open={formProps.openForm}
                title={formProps.title}
                size="l"
                className="max-w-[min(1180px,calc(100vw-2rem))] [&>div>div.relative]:!mt-4"
                footerClassName="!mt-4"
                onClose={() => formProps.closeForm(false)}
                loading={formProps.loading}
                alert={<HeaderAlert formProps={formProps} />}
                footer={<Footer formProps={formProps} />}
            >
                <div className="flex min-h-0 flex-col gap-3">
                    {formProps.employeeLabel && <p className="text-base font-semibold text-[#26000B]">{formProps.employeeLabel}</p>}

                    {formProps.timelineSegments.length > 0 && (
                        <Timeline
                            className="px-3"
                            segments={formProps.timelineSegments}
                            allowGaps
                            editableBoundaries
                            indefiniteDate={formProps.INDEFINITE_DATE}
                            indefiniteLabel={formProps.t('assign_hiring.table.indefinite')}
                            onBoundaryDateChange={formProps.handleBoundaryDateChange}
                            formatDate={formProps.formatTimelineDate}
                            onSegmentClick={({ segment }) => formProps.openEditDetail(Number(segment.id))}
                            segmentTooltip={(segment) => `${formProps.formatTimelineDate(segment.startDate)} → ${formProps.formatTimelineDate(segment.endDate)}`}
                            scale="equal"
                        />
                    )}

                    <div className="w-full min-w-0">
                        <RbTable<IHiringRow>
                            columns={formProps.columns}
                            rows={formProps.rows}
                            loading={formProps.loading || formProps.saving}
                            showToolbar={false}
                            paginate={false}
                            emptyText={formProps.t('assign_hiring.table.empty')}
                            density="compact"
                            bodyClassName="h-auto! max-h-28 min-h-50"
                        />
                    </div>
                </div>
            </FormShell>

            <PositionTreeDialog formProps={formProps} />
        </>
    );
};

export { AssignHiringDialog };
